import { Vec2, angleInArc, arcSweep, clamp } from './math';
import { entityBBox, textBBox } from './bbox';
import { DEFAULT_FLATTEN, entityPaths, type FlattenOptions } from './flatten';
import { splineData, splinePointAt } from './spline';
import type { Entity } from './types';

export interface ClosestResult {
  point: Vec2;
  distance: number;
}

export function closestPointOnSegment(a: Vec2, b: Vec2, p: Vec2): Vec2 {
  const ab = b.sub(a);
  const lenSq = ab.lenSq();
  if (lenSq < 1e-18) return a;
  const t = clamp(p.sub(a).dot(ab) / lenSq, 0, 1);
  return a.add(ab.scale(t));
}

export function distancePointSegment(a: Vec2, b: Vec2, p: Vec2): number {
  return closestPointOnSegment(a, b, p).distanceTo(p);
}

function closestPointOnBBox(bbox: ReturnType<typeof entityBBox>, p: Vec2): Vec2 {
  return Vec2.of(
    clamp(p.x, bbox.minX, bbox.maxX),
    clamp(p.y, bbox.minY, bbox.maxY),
  );
}

export function closestPointOnEntity(
  entity: Entity,
  p: Vec2,
  opts: FlattenOptions = DEFAULT_FLATTEN,
): ClosestResult | null {
  switch (entity.type) {
    case 'line': {
      const point = closestPointOnSegment(entity.a, entity.b, p);
      return { point, distance: point.distanceTo(p) };
    }
    case 'polyline': {
      let best = Infinity;
      let bestPoint: Vec2 | null = null;
      const n = entity.vertices.length;
      for (let i = 1; i < n; i++) {
        const a = entity.vertices[i - 1]!;
        const b = entity.vertices[i]!;
        const point = closestPointOnSegment(a, b, p);
        const d = point.distanceTo(p);
        if (d < best) {
          best = d;
          bestPoint = point;
        }
      }
      if (entity.closed && n > 2) {
        const point = closestPointOnSegment(entity.vertices[n - 1]!, entity.vertices[0]!, p);
        const d = point.distanceTo(p);
        if (d < best) {
          best = d;
          bestPoint = point;
        }
      }
      return bestPoint ? { point: bestPoint, distance: best } : null;
    }
    case 'circle': {
      const dir = p.sub(entity.center);
      if (dir.isZero(1e-12)) {
        const point = entity.center.add(Vec2.of(entity.radius, 0));
        return { point, distance: entity.radius };
      }
      const point = entity.center.add(dir.normalize().scale(entity.radius));
      return { point, distance: Math.abs(p.distanceTo(entity.center) - entity.radius) };
    }
    case 'arc': {
      const dir = p.sub(entity.center);
      const angle = dir.angle();
      if (angleInArc(angle, entity.startAngle, entity.endAngle, entity.ccw)) {
        const point = entity.center.add(dir.normalize().scale(entity.radius));
        return { point, distance: Math.abs(p.distanceTo(entity.center) - entity.radius) };
      }
      const p1 = entity.center.add(Vec2.polar(entity.radius, entity.startAngle));
      const p2 = entity.center.add(Vec2.polar(entity.radius, entity.endAngle));
      const d1 = p1.distanceTo(p);
      const d2 = p2.distanceTo(p);
      return d1 <= d2 ? { point: p1, distance: d1 } : { point: p2, distance: d2 };
    }
    case 'text': {
      const bbox = textBBox(entity);
      const point = closestPointOnBBox(bbox, p);
      return { point, distance: point.distanceTo(p) };
    }
    case 'hatch':
    case 'ellipse':
    case 'spline':
    case 'dimension': {
      let best = Infinity;
      let bestPoint: Vec2 | null = null;
      for (const path of entityPaths(entity, opts)) {
        const points = path.points;
        for (let i = 1; i < points.length; i++) {
          const point = closestPointOnSegment(points[i - 1]!, points[i]!, p);
          const d = point.distanceTo(p);
          if (d < best) {
            best = d;
            bestPoint = point;
          }
        }
      }
      return bestPoint ? { point: bestPoint, distance: best } : null;
    }
    default:
      return null;
  }
}

export function distanceToEntity(entity: Entity, p: Vec2): number {
  return closestPointOnEntity(entity, p)?.distance ?? Infinity;
}

interface LinearPrimitive {
  a: Vec2;
  b: Vec2;
}

interface RadialPrimitive {
  center: Vec2;
  radius: number;
  startAngle?: number;
  endAngle?: number;
  ccw?: boolean;
}

function linearPrimitives(entity: Entity, opts: FlattenOptions): LinearPrimitive[] {
  switch (entity.type) {
    case 'line':
      return [{ a: entity.a, b: entity.b }];
    case 'polyline': {
      const out: LinearPrimitive[] = [];
      const v = entity.vertices;
      for (let i = 1; i < v.length; i++) out.push({ a: v[i - 1]!, b: v[i]! });
      if (entity.closed && v.length > 2) out.push({ a: v[v.length - 1]!, b: v[0]! });
      return out;
    }
    case 'ellipse':
    case 'hatch':
    case 'spline':
    case 'dimension': {
      const out: LinearPrimitive[] = [];
      for (const path of entityPaths(entity, opts)) {
        const pts = path.closed ? [...path.points, path.points[0]!] : path.points;
        for (let i = 1; i < pts.length; i++) out.push({ a: pts[i - 1]!, b: pts[i]! });
      }
      return out;
    }
    default:
      return [];
  }
}

function radialPrimitives(entity: Entity): RadialPrimitive[] {
  if (entity.type === 'circle') return [{ center: entity.center, radius: entity.radius }];
  if (entity.type === 'arc') {
    return [
      {
        center: entity.center,
        radius: entity.radius,
        startAngle: entity.startAngle,
        endAngle: entity.endAngle,
        ccw: entity.ccw,
      },
    ];
  }
  return [];
}

function onRadial(radial: RadialPrimitive, p: Vec2): boolean {
  if (radial.startAngle === undefined || radial.endAngle === undefined) return true;
  const angle = p.sub(radial.center).angle();
  if (angleInArc(angle, radial.startAngle, radial.endAngle, radial.ccw ?? true)) return true;
  const p1 = radial.center.add(Vec2.polar(radial.radius, radial.startAngle));
  const p2 = radial.center.add(Vec2.polar(radial.radius, radial.endAngle));
  return p1.distanceTo(p) < 1e-7 || p2.distanceTo(p) < 1e-7;
}

export function intersectSegments(
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  p4: Vec2,
  eps = 1e-12,
): Vec2 | null {
  const r = p2.sub(p1);
  const s = p4.sub(p3);
  const denom = r.cross(s);
  if (Math.abs(denom) < eps) return null;
  const diff = p3.sub(p1);
  const t = diff.cross(s) / denom;
  const u = diff.cross(r) / denom;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  return p1.add(r.scale(t));
}

/** How far apart two primitives may be and still count as touching (mm). */
const TOUCH_SLACK = 1e-6;

/** Infinite-line/circle intersections restricted to the segment and the arc range. */
function segmentRadial(seg: LinearPrimitive, radial: RadialPrimitive): Vec2[] {
  const d = seg.b.sub(seg.a);
  const f = seg.a.sub(radial.center);
  const a = d.dot(d);
  if (a < 1e-18) return [];
  const b = 2 * f.dot(d);
  const c = f.dot(f) - radial.radius * radial.radius;
  const disc = b * b - 4 * a * c;
  // A line that only *touches* the circle gives a discriminant of (almost) zero: keep it, so the
  // contact can still be cut there. The slack is a distance, so it is scaled by the segment length
  // (`a`) the way the discriminant itself is.
  if (disc < 0) {
    const sagitta = (Math.sqrt(-disc) / (2 * a)) * Math.sqrt(a);
    if (sagitta > TOUCH_SLACK) return [];
    const contact = seg.a.add(d.scale(-b / (2 * a)));
    return onRadial(radial, contact) ? [contact] : [];
  }
  const sqrt = Math.sqrt(disc);
  const out: Vec2[] = [];
  for (const t of [(-b - sqrt) / (2 * a), (-b + sqrt) / (2 * a)]) {
    if (t < -1e-9 || t > 1 + 1e-9) continue;
    const p = seg.a.add(d.scale(t));
    if (onRadial(radial, p)) out.push(p);
  }
  return out;
}

/** How far apart two round primitives may be and still count as touching (mm). */
function radialRadial(r1: RadialPrimitive, r2: RadialPrimitive): Vec2[] {
  const d = r1.center.distanceTo(r2.center);
  if (d < 1e-12) return [];
  // `+ TOUCH_SLACK`: a tangency solved by a constraint lands a fraction of a micron out, and the
  // contact would otherwise be lost — which is what stopped two touching circles from being cut.
  if (d > r1.radius + r2.radius + TOUCH_SLACK) return [];
  if (d < Math.abs(r1.radius - r2.radius) - TOUCH_SLACK) return [];

  const a = (r1.radius * r1.radius - r2.radius * r2.radius + d * d) / (2 * d);
  const hSq = r1.radius * r1.radius - a * a;
  const h = hSq <= 0 ? 0 : Math.sqrt(hSq);
  const dir = r2.center.sub(r1.center).div(d);
  const base = r1.center.add(dir.scale(a));
  const normal = dir.perp().scale(h);
  const candidates = h < 1e-12 ? [base] : [base.add(normal), base.sub(normal)];
  return candidates.filter((p) => onRadial(r1, p) && onRadial(r2, p));
}

/** All intersection points between two entities (analytic where possible). */
export function intersectEntities(
  a: Entity,
  b: Entity,
  opts: FlattenOptions = DEFAULT_FLATTEN,
): Vec2[] {
  const results: Vec2[] = [];
  const push = (p: Vec2): void => {
    if (!results.some((q) => q.distanceTo(p) < 1e-6)) results.push(p);
  };

  const linearA = linearPrimitives(a, opts);
  const linearB = linearPrimitives(b, opts);
  const radialA = radialPrimitives(a);
  const radialB = radialPrimitives(b);

  for (const s1 of linearA) {
    for (const s2 of linearB) {
      const p = intersectSegments(s1.a, s1.b, s2.a, s2.b);
      if (p) push(p);
    }
    for (const circle of radialB) for (const p of segmentRadial(s1, circle)) push(p);
  }
  for (const s2 of linearB) {
    for (const circle of radialA) for (const p of segmentRadial(s2, circle)) push(p);
  }
  for (const c1 of radialA) for (const c2 of radialB) for (const p of radialRadial(c1, c2)) push(p);

  return results;
}

/** Point-in-polygon (even-odd) used by the hatch pick-point tool. */
export function pointInLoops(loops: readonly Vec2[][], p: Vec2): boolean {
  let inside = false;
  for (const loop of loops) {
    for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
      const pi = loop[i]!;
      const pj = loop[j]!;
      const intersects =
        pi.y > p.y !== pj.y > p.y &&
        p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x;
      if (intersects) inside = !inside;
    }
  }
  return inside;
}

export function entityMidpoints(entity: Entity): Vec2[] {
  switch (entity.type) {
    case 'line':
      return [Vec2.mid(entity.a, entity.b)];
    case 'polyline': {
      const out: Vec2[] = [];
      const v = entity.vertices;
      for (let i = 1; i < v.length; i++) out.push(Vec2.mid(v[i - 1]!, v[i]!));
      if (entity.closed && v.length > 2) out.push(Vec2.mid(v[v.length - 1]!, v[0]!));
      return out;
    }
    case 'arc': {
      const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
      const direction = entity.ccw ? 1 : -1;
      const mid = entity.startAngle + (direction * sweep) / 2;
      return [entity.center.add(Vec2.polar(entity.radius, mid))];
    }
    case 'dimension':
      return [Vec2.mid(entity.p1, entity.p2)];
    case 'spline': {
      const data = splineData(entity);
      return data.points.length > 0 ? [splinePointAt(data, 0.5)] : [];
    }
    default:
      return [];
  }
}

export function entityEndpoints(entity: Entity): Vec2[] {
  switch (entity.type) {
    case 'line':
      return [entity.a, entity.b];
    case 'polyline':
      return [...entity.vertices];
    case 'arc':
      return [
        entity.center.add(Vec2.polar(entity.radius, entity.startAngle)),
        entity.center.add(Vec2.polar(entity.radius, entity.endAngle)),
      ];
    case 'spline': {
      const data = splineData(entity);
      if (data.points.length === 0) return [];
      if (entity.closed) return [...entity.controlPoints];
      return [splinePointAt(data, 0), splinePointAt(data, 1)];
    }
    case 'dimension':
      return [entity.p1, entity.p2];
    default:
      return [];
  }
}

export function entityCenters(entity: Entity): Vec2[] {
  switch (entity.type) {
    case 'circle':
    case 'arc':
    case 'ellipse':
      return [entity.center];
    case 'polyline': {
      const bbox = entityBBox(entity);
      return entity.closed ? [bbox.center] : [];
    }
    case 'spline': {
      const bbox = entityBBox(entity);
      return entity.closed ? [bbox.center] : [];
    }
    default:
      return [];
  }
}

const QUADRANT_ANGLES: readonly number[] = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

/**
 * The quadrant points of a round shape — right, top, left, bottom, the four places its axes cross
 * it. Together with `entityCenters` that is the five grips a circle offers by default.
 */
export function entityQuadrants(entity: Entity): Vec2[] {
  switch (entity.type) {
    case 'circle': {
      const { center, radius } = entity;
      return [
        Vec2.of(center.x + radius, center.y),
        Vec2.of(center.x, center.y + radius),
        Vec2.of(center.x - radius, center.y),
        Vec2.of(center.x, center.y - radius),
      ];
    }
    case 'arc': {
      const out: Vec2[] = [];
      for (const angle of QUADRANT_ANGLES) {
        if (!angleInArc(angle, entity.startAngle, entity.endAngle, entity.ccw)) continue;
        out.push(entity.center.add(Vec2.polar(entity.radius, angle)));
      }
      return out;
    }
    case 'ellipse':
      return QUADRANT_ANGLES.map((angle) =>
        Vec2.of(Math.cos(angle) * entity.rx, Math.sin(angle) * entity.ry)
          .rotate(entity.rotation)
          .add(entity.center),
      );
    default:
      return [];
  }
}
